function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
const animationStates = new WeakMap();
const animationData = new WeakMap();
function checkVisibility(node) {
    const { body } = node.ownerDocument;
    if (typeof body.checkVisibility === 'function') {
        return body.checkVisibility();
    } else {
        const rect = body.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
    }
}
function getAnimationState(node) {
    let state = animationStates.get(node);
    if (state === undefined) {
        state = {
            activeAnimations: new Map(),
            appliedStack: [],
            locked: false
        };
        animationStates.set(node, state);
    }
    return state;
}
function getCurrentAnimation(node, prop) {
    return getAnimationState(node).activeAnimations.get(prop) || null;
}
function getFinalProperty(node, prop) {
    const state = getAnimationState(node);
    const animation = state.activeAnimations.get(prop);
    if (animation) {
        const data = animationData.get(animation);
        if (data) {
            return data.direction === 'reverse' ? data.from : data.to;
        }
    }
    return null;
}
function setLockState(node, flag) {
    const state = getAnimationState(node);
    state.locked = flag;
}
function getLockState(node) {
    return getAnimationState(node).locked;
}
function setCurrentAnimation(node, prop, animation, data) {
    const state = getAnimationState(node);
    state.activeAnimations.set(prop, animation);
    if (animation && data) {
        animationData.set(animation, data);
    }
}
function cancelCurrentAnimation(node, prop) {
    const anim = getCurrentAnimation(node, prop);
    if (anim) {
        anim.cancel();
    }
    setCurrentAnimation(node, prop, null);
}
// The next two functions implement an animation stack. This is a critical piece
// in the system for transitions, and allows deriving the right style props to set
// when adding and removing states. Will document more later, for now you can
// see a bunch of the edge case here:
// https://excalidraw.com/#json=5692055233757184,dSIR11OVksqSlFMlKlQiEg
export function removeAnimation(node, id) {
    const stack = getAnimationState(node).appliedStack;
    const index = stack.findIndex((anim)=>anim.id === id);
    if (index === -1) {
        return null;
    } else {
        const [{ from }] = stack.splice(index, 1);
        const props = Object.keys(from);
        for(let i = index; i < stack.length; i++){
            const desc = stack[i].from;
            props.forEach((prop)=>{
                if (prop in desc) {
                    desc[prop] = from[prop];
                    delete from[prop];
                }
            });
        }
        return from;
    }
}
export function pushAnimation(node, keyframe) {
    const stack = getAnimationState(node).appliedStack;
    const { ...from } = keyframe;
    Object.keys(from).forEach((prop)=>{
        from[prop] = getFinalProperty(node, prop) || from[prop];
    });
    const id = Math.random();
    stack.push({
        id,
        from
    });
    return ()=>removeAnimation(node, id);
}
// Get a promise that resolve when this animation finishes. There is an
// important difference between this and the `animation.finished` promised, read
// below
function getAnimationFinished(animation) {
    const { playId: previousPlayId } = animationData.get(animation) || {};
    return new Promise((resolve, _)=>{
        animation.addEventListener('finish', ()=>{
            const { playId } = animationData.get(animation) || {};
            // We ignore all `finish` events that come from ALL animations made after
            // the point in which `getAnimationFinished` was called. Animations can be
            // reused (reversed), and we consider that animation a new one so this
            // ignores `finish` events if animations have been reused.
            if (previousPlayId === playId) {
                resolve(animation);
            }
        });
    });
}
function filterStyle(style, properties) {
    return properties.reduce((obj, property)=>{
        obj[property] = style[property];
        return obj;
    }, {});
}
// `reverseAnimation` and `animateStyle` ended up being complementary functions.
// At a high-level, the animation system is always playing new animations. However,
// internally we try to reverse animations when possible for a seamless transition
// of states. `reverseAnimation` attempts this and if it fails (it's not always safe
// to do so), we end up calling `animateStyle` instead. Both these functions have
// similar semantics: the prop should end up on a specific value, and when
// the animation is finished they should update the style of the node.
function reverseAnimation(node, prop, // eslint-disable-next-line @typescript-eslint/no-explicit-any
value) {
    const state = getAnimationState(node);
    const animation = getCurrentAnimation(node, prop);
    const data = animation && animationData.get(animation);
    if (state && animation && data) {
        const nextValue = data.direction === 'normal' ? data.from : data.to;
        // TODO: This does not work when one value is read from the DOM and one is
        // not. They might be formatted differently, for example `translateX(450px)`
        // will come back from the DOM as `matrix(1, 0, 0, 1, 450, 0)`.
        //
        // This usually doesn't impact in/out animations, but it will impact
        // transitions. For example: if animating `transform`, it will apply the
        // styles once its done. If you apply another transition and then remove it,
        // it wants to go back to the previously applied styles. But when it did the
        // second animation, it recorded the current styles from the DOM, so it will
        // compare the user-supplied value with the DOM-formatted style.
        //
        // The result is it won't reverse the animation when applying two
        // transitions in order, and then removing the second one. That causes
        // less-than-ideal motion because it resets the duration/easing curves.
        // Should be fixable though.
        if (value === nextValue) {
            // Reverse it
            animation.reverse();
            // Update global state
            data.direction = data.direction === 'normal' ? 'reverse' : 'normal';
            data.playId = Math.random();
            state.activeAnimations.set(prop, animation);
            // We need to do this here to! We're just reversing the animation,
            // which needs to do everything `animateStyle` does. The finished
            // event in there won't get fired because we've "taken over"
            // We should abstract this out better
            getAnimationFinished(animation).then(()=>{
                // @ts-expect-error figure out keys
                node.style[prop] = value;
                // We need to cancel the animation or else the browser will keep it around
                // in the active animations list (i.e. document.getAnimations()) and the
                // "to" state styles will always persist.
                cancelCurrentAnimation(node, prop);
            });
            return animation;
        }
    }
    return null;
}
function animateStyle(node, prop, keyframes, options) {
    // Automatically cancel previous animation
    cancelCurrentAnimation(node, prop);
    const animation = node.animate({
        [prop]: keyframes
    }, {
        delay: options.delay || 0,
        direction: options.direction,
        duration: options.duration || 0,
        easing: options.easing || 'ease-in-out',
        fill: 'backwards'
    });
    const initialValue = keyframes[0];
    const finalValue = keyframes[keyframes.length - 1];
    setCurrentAnimation(node, prop, animation, {
        from: initialValue,
        to: finalValue,
        direction: options.direction,
        playId: Math.random()
    });
    getAnimationFinished(animation).then(()=>{
        // @ts-expect-error figure out keys
        node.style[prop] = options.direction === 'reverse' ? initialValue : finalValue;
        cancelCurrentAnimation(node, prop);
    });
    return animation;
}
export function toAnimationTask(tasks) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return animationTask(Array.prototype.concat.apply([], tasks.map((task)=>task.animations)), Array.prototype.concat.apply([], tasks.map((task)=>task.removeFunctions)));
}
function animationTask(animations, removeFunctions) {
    return {
        animations,
        removeFunctions,
        removeAndPlay () {
            return toAnimationTask(removeFunctions.map((remove)=>remove()));
        },
        cancel: ()=>{
            // TODO: This should go through `cancelCurrentAnimation` or something else
            // so that the active animation state is updated (necessary if this is
            // currently the active animation)
            animations.forEach((anim)=>anim.cancel());
        },
        getFinished: ()=>{
            return Promise.all(animations.map((anim)=>getAnimationFinished(anim))).then(()=>{
            // Ignore the returned value
            });
        }
    };
}
export function emptyAnimationTask() {
    return animationTask([], []);
}
export class NodeAnimation {
    play({ reverse, backTo, push } = {}) {
        const node = this.nodeRef.current;
        // If the node's owning body is not visible, then we won't be able to run animations on it.
        // This can cause problems, for example AnimatePresence will retain its children indefinitely.
        // This is normally invisible, but it leaks memory, and in the case of iframes, it can cause
        // the iframe to continue to be visible even after it should have been removed.
        if (!node || !checkVisibility(node)) {
            return emptyAnimationTask();
        }
        const reverseMode = reverse || this.options.reverse;
        if (getLockState(node)) {
            return emptyAnimationTask();
        }
        let from = filterStyle(getComputedStyle(node), Object.keys(this.styles));
        let to = this.styles;
        if (backTo) {
            to = backTo;
        }
        let removeStyles = null;
        if (push) {
            removeStyles = pushAnimation(node, reverseMode === 'keyframes' ? to : from);
        }
        if (reverseMode === 'keyframes' || reverseMode === 'effects') {
            // We reverse these even for the "effects" option, because
            // that means to run from the current state to the final state
            // (keyframes are not reversed) but since the whole animation
            // is run reversed, we need to swap these
            const swapped = to;
            to = from;
            from = swapped;
        }
        const options = {
            ...this.options
        };
        options.direction = reverseMode === 'effects' ? 'reverse' : 'normal';
        this.direction = options.direction;
        const animations = Object.keys(to).map((prop)=>{
            // First, attempt to reverse the animation but if that fails, fall back to
            // creating a new one
            const reversed = reverseAnimation(node, prop, options.direction === 'normal' ? to[prop] : from[prop]);
            return reversed || animateStyle(node, prop, [
                from[prop],
                to[prop]
            ], options);
        });
        this.latestTask = animationTask(animations, [
            ()=>{
                if (removeStyles) {
                    const backTo = removeStyles();
                    if (backTo) {
                        return this.play({
                            reverse: 'effects',
                            backTo
                        });
                    }
                }
                return emptyAnimationTask();
            }
        ]);
        return this.latestTask;
    }
    removeAndPlay() {
        var _this_latestTask;
        return ((_this_latestTask = this.latestTask) === null || _this_latestTask === void 0 ? void 0 : _this_latestTask.removeAndPlay()) || emptyAnimationTask();
    }
    cancel() {
        var _this_latestTask;
        (_this_latestTask = this.latestTask) === null || _this_latestTask === void 0 ? void 0 : _this_latestTask.cancel();
    }
    getFinished() {
        var _this_latestTask;
        return ((_this_latestTask = this.latestTask) === null || _this_latestTask === void 0 ? void 0 : _this_latestTask.getFinished()) || Promise.resolve();
    }
    lockNode() {
        const node = this.nodeRef.current;
        if (node) {
            setLockState(node, true);
        }
    }
    unlockNode() {
        const node = this.nodeRef.current;
        if (node) {
            setLockState(node, false);
        }
    }
    /* eslint-enable lines-between-class-members */ constructor(timeline, nodeRef, styles, options){
        /* eslint-disable lines-between-class-members */ _define_property(this, "timeline", void 0);
        _define_property(this, "nodeRef", void 0);
        _define_property(this, "styles", void 0);
        _define_property(this, "options", void 0);
        _define_property(this, "direction", void 0);
        _define_property(this, "latestTask", void 0);
        this.timeline = timeline;
        this.nodeRef = nodeRef;
        this.styles = styles;
        this.options = options;
        // TODO: Is this used for anything?
        this.direction = 'normal';
    }
}

//# sourceMappingURL=animate.js.map